import { useState, useRef } from "react";
import { Button } from "../ui/button";
import { adminFetch } from "../../lib/adminApi";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Edit2 } from "lucide-react";
import { ProjectEditor } from "./ProjectEditor";

interface ProjectItem {
  id: string;
  title_en: string;
  title_tr: string;
  date: string;
  version: string;
  tags_en: string;
  tags_tr: string;
  summary_en: string;
  summary_tr: string;
  detailed_content_en: string;
  detailed_content_tr: string;
  tech_stack: string;
  links: string;
  images: string;
  metrics: string;
  sort_order: number;
}

interface AdminProjectsTabProps {
  projects: ProjectItem[];
  onRefresh: () => void;
}

function parseJsonArray(jsonStr: string | undefined): any[] {
  try {
    return jsonStr ? JSON.parse(jsonStr) : [];
  } catch {
    return [];
  }
}

interface ProjectRowActionsProps {
  project: ProjectItem;
  idx: number;
  total: number;
  onMove: (idx: number, direction: "up" | "down") => void;
  onEdit: (project: ProjectItem) => void;
  onDelete: (id: string) => void;
}

function ProjectRowActions({ project, idx, total, onMove, onEdit, onDelete }: ProjectRowActionsProps) {
  return (
    <>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "up")}
            disabled={idx === 0}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowUp className="h-4.5 w-4.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            onClick={() => onMove(idx, "down")}
            disabled={idx === total - 1}
            className="h-7 w-7 text-muted-foreground hover:text-foreground"
          >
            <ArrowDown className="h-4.5 w-4.5" />
          </Button>
        </div>
      </td>
      <td className="p-4 text-center">
        <div className="flex items-center justify-center gap-2">
          <Button
            size="icon"
            variant="outline"
            onClick={() => onEdit(project)}
            className="h-8 w-8 border-border/60 hover:bg-brand-primary/10 hover:text-brand-primary text-muted-foreground"
          >
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button
            size="icon"
            variant="outline"
            onClick={() => onDelete(project.id)}
            className="h-8 w-8 border-border/60 hover:bg-destructive/10 hover:text-destructive text-muted-foreground"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </>
  );
}

interface ProjectRowProps {
  project: ProjectItem;
  idx: number;
  total: number;
  onMove: (idx: number, direction: "up" | "down") => void;
  onEdit: (project: ProjectItem) => void;
  onDelete: (id: string) => void;
}

function ProjectRow({ project, idx, total, onMove, onEdit, onDelete }: ProjectRowProps) {
  return (
    <tr className="hover:bg-muted/10 transition-colors">
      <td className="p-4 text-center font-mono font-bold text-muted-foreground">
        {idx + 1}
      </td>
      <td className="p-4 w-20 text-center">
        <span className="inline-block text-[10px] uppercase font-bold text-brand-secondary bg-brand-secondary/5 px-2 py-0.5 rounded border border-border/40 select-none">
          {project.version}
        </span>
      </td>
      <td className="p-4 w-28 font-mono text-xs text-muted-foreground leading-snug">
        {project.date}
      </td>
      <td className="p-4 max-w-xs space-y-1">
        <p className="font-semibold text-foreground leading-snug">
          {project.title_en}
        </p>
        {project.title_tr && project.title_tr !== project.title_en && (
          <p className="text-xs text-muted-foreground italic">
            {project.title_tr}
          </p>
        )}
      </td>
      <td className="p-4 max-w-md space-y-1">
        <p className="text-xs text-foreground/85 line-clamp-2 leading-relaxed">
          {project.summary_en}
        </p>
        {project.summary_tr && project.summary_tr !== project.summary_en && (
          <p className="text-[11px] text-muted-foreground/80 italic line-clamp-2 leading-relaxed">
            {project.summary_tr}
          </p>
        )}
      </td>
      <ProjectRowActions
        project={project}
        idx={idx}
        total={total}
        onMove={onMove}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </tr>
  );
}

interface ProjectListProps {
  sortedProjects: ProjectItem[];
  onMove: (index: number, direction: "up" | "down") => void;
  onEdit: (project: ProjectItem) => void;
  onDelete: (id: string) => void;
}

function ProjectList({ sortedProjects, onMove, onEdit, onDelete }: ProjectListProps) {
  if (sortedProjects.length === 0) {
    return (
      <div className="text-center py-12 border border-dashed border-border/60 rounded-2xl bg-card/40">
        <p className="text-sm font-semibold text-muted-foreground">
          No projects configured. Click the button above to add one.
        </p>
      </div>
    );
  }

  return (
    <div className="border border-border/60 bg-card rounded-2xl overflow-hidden shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse font-sans">
          <thead>
            <tr className="bg-muted/40 border-b border-border/60 text-xs font-bold uppercase tracking-wider text-muted-foreground">
              <th className="p-4 w-12 text-center">#</th>
              <th className="p-4 w-20 text-center">Version</th>
              <th className="p-4 w-28">Date</th>
              <th className="p-4">Title (EN / TR)</th>
              <th className="p-4">Summary (EN / TR)</th>
              <th className="p-4 w-28 text-center">Order</th>
              <th className="p-4 w-36 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/40 text-sm">
            {sortedProjects.map((project, idx) => (
              <ProjectRow
                key={project.id}
                project={project}
                idx={idx}
                total={sortedProjects.length}
                onMove={onMove}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

import { useAdminProjects } from "./useAdminProjects";

export function AdminProjectsTab({ projects, onRefresh }: AdminProjectsTabProps) {
  const {
    sortedProjects,
    editingProject,
    setEditingProject,
    uploading,
    fileInputRef,
    startAdd,
    handleImageUpload,
    handleSave,
    handleDelete,
    handleMove,
    activeDraft,
    setActiveDraft,
    handleSaveDraft,
    handleDiscardDraft
  } = useAdminProjects(projects, onRefresh);

  return (
    <div className="space-y-6 text-left">
      <div className="flex justify-between items-center pb-2 border-b border-border/80">
        <div>
          <h4 className="text-xl font-bold text-foreground uppercase tracking-wider">Showcase Projects</h4>
          <p className="text-xs text-muted-foreground mt-0.5">Manage and showcase custom engineering projects</p>
        </div>
        {!editingProject && (
          <Button onClick={startAdd} className="bg-brand-primary text-white">
            <Plus className="h-4 w-4 mr-2" /> Add Project
          </Button>
        )}
      </div>

      {editingProject ? (
        <ProjectEditor
          editingProject={editingProject}
          setEditingProject={setEditingProject}
          onSave={handleSave}
          onCancel={() => setEditingProject(null)}
          uploading={uploading}
          fileInputRef={fileInputRef}
          handleImageUpload={handleImageUpload}
          getParsedList={parseJsonArray}
          activeDraft={activeDraft}
          onSaveDraft={handleSaveDraft}
          onDiscardDraft={handleDiscardDraft}
          liveProject={projects.find((p) => p.id === editingProject.id)}
        />
      ) : (
        <ProjectList
          sortedProjects={sortedProjects}
          onMove={handleMove}
          onEdit={(proj) => {
            setEditingProject(proj);
            adminFetch(`/api/admin/drafts?item_type=project&item_id=${proj.id}`)
              .then((res) => {
                if (res?.success && res.draft) {
                  setActiveDraft(res.draft);
                  setEditingProject(res.draft);
                  toast.info("Unpublished staged draft loaded for editing.");
                }
              })
              .catch((err) => console.error("Failed to load draft:", err));
          }}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}

export default AdminProjectsTab;
